import { Component, OnDestroy, OnInit } from '@angular/core';
import { USAGE_GUIDELINES_URL } from '../../shared/usage-guidelines';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthUser } from '../../shared/models/user.model';
import { BackendChannelService, BackendChannelType } from '../../shared/services/backend-channel.service';
import { CustomUtils } from '../../shared/services/custom-utils';
import { UsersService } from '../../shared/services/users.service';

@Component({
  standalone: false,
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy {
  readonly usageGuidelinesUrl = USAGE_GUIDELINES_URL;
  user: AuthUser;
  capitializedUserShortname = '';

  private $backendChanel: Subscription;
  private $authUpdates: Subscription;
  currentBackendChanel: BackendChannelType;

  shouldShowLegalDocumentsPage = false;
  private hasExternalUserId = false;
  private hasVerifiedCompanyDetails = false;
  private isExternalUserIdLoading = false;

  constructor(
    private userService: UsersService,
    private router: Router,
    backendChannel: BackendChannelService
  ) {
    this.$backendChanel = backendChannel.$current.subscribe(ch => {
      this.currentBackendChanel = ch;
    });
  }

  ngOnInit(): void {
    this.userService.getAuthUser().then(user => {
      this.applyUser(user);
    });
    this.$authUpdates = this.userService.authUpdates.subscribe(user => {
      this.applyUser(user);
    });
  }

  logout(): void {
    this.userService.logout(['/login'], ['/users']).then(url => {
      this.router.navigate(url).finally(() => {});
    });
  }

  is(roles: string[], onlyEnabled = false) {
    if (!this.user) {
      return false;
    }
    if (onlyEnabled && !this.user.enabled) {
      return false;
    }
    let result = false;
    for (const role of roles) {
      if (role === this.user.role) {
        result = true;
        break;
      }
    }
    return result;
  }

  private applyUser(user: AuthUser): void {
    if (!user) {
      return;
    }
    this.user = user;
    this.capitializedUserShortname = CustomUtils.getInitials(user.fullName);
    this.hasVerifiedCompanyDetails = !!user?.companyDetailsVerified;
    if (!this.hasExternalUserId) {
      this.refreshExternalUserId();
    }
    this.updateLegalDocumentsVisibility();
  }

  private refreshExternalUserId(): void {
    if (this.isExternalUserIdLoading) {
      return;
    }
    this.isExternalUserIdLoading = true;
    this.userService
      .loadExternalUserId()
      .then(externalUserId => {
        this.hasExternalUserId = !!externalUserId;
        this.updateLegalDocumentsVisibility();
        this.isExternalUserIdLoading = false;
      })
      .catch(() => {
        this.hasExternalUserId = false;
        this.updateLegalDocumentsVisibility();
        this.isExternalUserIdLoading = false;
      });
  }

  private updateLegalDocumentsVisibility(): void {
    // Mirror the legal-documents route guard (requireExternalUserId + requireCompanyDetailsVerified). Being onboarded is
    // not enough: a re-acceptance company-details change resets companyDetailsVerified, so the link must hide until the
    // updated details are re-verified — otherwise it would point at a page the guard now blocks.
    this.shouldShowLegalDocumentsPage = this.hasExternalUserId && this.hasVerifiedCompanyDetails;
  }

  ngOnDestroy() {
    this.$backendChanel.unsubscribe();
    if (this.$authUpdates) {
      this.$authUpdates.unsubscribe();
    }
  }
}
